import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pie } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useAuth } from "authContext";
import api from "common/api";
import { toast } from "react-toastify";
import { Term } from "common/types";
import { useAcademicYear } from "./AcademicYearContext";

const DatePicker = ReactDatePicker as any;

ChartJS.register(ArcElement, Tooltip, Legend);

interface PastTermsTableProps {
  terms?: Term[];
}

const PastTermsTable: React.FC<PastTermsTableProps> = ({ terms }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const { userProfile } = useAuth();
  const { selectedAcademicYear, getAcademicYear, getCurrentAcademicYear } =
    useAcademicYear();
  const isTeacher = userProfile?.role === "TEACHER";
  const isPrincipalOrAccountant = ["PRINCIPAL", "ACCOUNTANT"].includes(
    userProfile?.role || "",
  );
  const [calendarTerms, setCalendarTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        setLoading(true);
        const response = await api.get("/calendars/?status=PAID");
        setCalendarTerms(response.data);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
        toast.error("Greška prilikom dohvaćanja termina");
      } finally {
        setLoading(false);
      }
    };

    if (!terms || terms.length === 0) {
      fetchCalendarData();
    } else {
      setCalendarTerms(terms);
    }
  }, [terms]);

  const calculateDurationMinutes = (
    startTime: string,
    endTime: string,
  ): number => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  };

  const calculateDurationHours = (
    startTime: string,
    endTime: string,
  ): number => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return parseFloat(
      ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toFixed(2),
    );
  };

  const columns = [
    {
      name: "Nastavnik",
      selector: (row: Term) =>
        `${row.teacher.first_name} ${row.teacher.last_name}`,
      sortable: true,
      omit: isTeacher,
    },
    {
      name: "Učenik",
      selector: (row: Term) =>
        `${row.student?.first_name} ${row.student?.last_name}`,
      sortable: true,
    },
    {
      id: "date",
      name: "Datum",
      selector: (row: Term) => new Date(row.start).getTime(),
      format: (row: Term) => new Date(row.start).toLocaleString("hr"),
      sortable: true,
    },
    {
      name: "Predmet",
      selector: (row: Term) => row.subject?.name || "Nepoznato",
      sortable: true,
    },
    {
      name: "Trajanje (min)",
      selector: (row: Term) => calculateDurationMinutes(row.start, row.end),
      sortable: true,
    },
  ];

  const teacherOptions = isTeacher
    ? []
    : Array.from(
        new Set(
          calendarTerms
            .map(
              (term) => `${term.teacher.first_name} ${term.teacher.last_name}`,
            )
            .filter((name) => name !== "Skriveni Testni Nastavnik"),
        ),
      ).sort();

  const filteredData = calendarTerms.filter((term) => {
    const teacherName = `${term.teacher.first_name} ${term.teacher.last_name}`;

    // Hide "Skriveni Testni Nastavnik"
    if (teacherName === "Skriveni Testni Nastavnik") return false;

    const termDate = new Date(term.start);

    // Filter by academic year
    if (selectedAcademicYear !== "all") {
      const termAcademicYear = getAcademicYear(termDate);
      const targetYear =
        selectedAcademicYear === "current"
          ? getCurrentAcademicYear()
          : selectedAcademicYear;
      if (termAcademicYear !== targetYear) return false;
    }

    if (startDate && termDate < startDate) return false;
    if (endDate && termDate > endDate) return false;
    if (!isTeacher && selectedTeacher) {
      if (teacherName !== selectedTeacher) return false;
    }
    return true;
  });

  const availableMonths = Array.from(
    new Set(
      calendarTerms.map((term) =>
        new Date(term.start).toLocaleString("hr", {
          year: "numeric",
          month: "long",
        }),
      ),
    ),
  ).sort();

  const getSubjectTotals = () => {
    const subjectTotals: { [key: string]: number } = {};

    calendarTerms
      .filter((term) => {
        const teacherName = `${term.teacher.first_name} ${term.teacher.last_name}`;
        if (teacherName === "Skriveni Testni Nastavnik") return false;

        const termMonth = new Date(term.start).toLocaleString("hr", {
          year: "numeric",
          month: "long",
        });
        return selectedMonth ? termMonth === selectedMonth : false;
      })
      .forEach((term) => {
        const subjectName = term.subject?.name || "Nepoznato";
        const duration = calculateDurationHours(term.start, term.end);

        subjectTotals[subjectName] =
          (subjectTotals[subjectName] || 0) + duration;
      });

    return subjectTotals;
  };

  const getTeacherTotals = () => {
    const teacherTotals: { [key: string]: number } = {};

    calendarTerms.forEach((term) => {
      const teacherName = `${term.teacher.first_name} ${term.teacher.last_name}`;

      // Skip "Skriveni Testni Nastavnik"
      if (teacherName === "Skriveni Testni Nastavnik") return;

      const duration = calculateDurationHours(term.start, term.end);

      teacherTotals[teacherName] = (teacherTotals[teacherName] || 0) + duration;
    });

    return teacherTotals;
  };

  const subjectTotals = getSubjectTotals();
  const teacherTotals = getTeacherTotals();

  const pieChartSubjectData = {
    labels: Object.keys(subjectTotals),
    datasets: [
      {
        label: "Ukupno sati",
        data: Object.values(subjectTotals),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const pieChartTeacherData = {
    labels: Object.keys(teacherTotals),
    datasets: [
      {
        label: "Ukupno sati",
        data: Object.values(teacherTotals),
        backgroundColor: [
          "rgba(99, 255, 132, 0.6)",
          "rgba(162, 54, 235, 0.6)",
          "rgba(206, 255, 86, 0.6)",
          "rgba(192, 75, 192, 0.6)",
          "rgba(102, 153, 255, 0.6)",
        ],
        borderColor: [
          "rgba(99, 255, 132, 1)",
          "rgba(162, 54, 235, 1)",
          "rgba(206, 255, 86, 1)",
          "rgba(192, 75, 192, 1)",
          "rgba(102, 153, 255, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div>
      <h2 className="mt-3">Svi plaćeni termini poduka</h2>
      <div className="d-flex gap-3 mb-3 flex-wrap">
        <div>
          <label>Početni datum: </label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            dateFormat="dd.MM.yyyy"
            className="form-control"
            placeholderText="Odaberite početni datum"
          />
        </div>
        <div>
          <label>Završni datum: </label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="dd.MM.yyyy"
            className="form-control"
            placeholderText="Odaberite završni datum"
          />
        </div>
        {!isTeacher && teacherOptions.length > 0 && (
          <div className="d-flex align-items-center">
            <label className="me-2">Nastavnik:</label>
            <select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              className="form-control"
            >
              <option value="">Svi nastavnici</option>
              {teacherOptions.map((teacher) => (
                <option key={teacher} value={teacher}>
                  {teacher}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        defaultSortFieldId="date"
        defaultSortAsc={false}
        pagination
        progressPending={loading}
        progressComponent={<div>Učitavanje...</div>}
        noDataComponent="Nema dostupnih prošlih termina"
      />

      {isPrincipalOrAccountant && (
        <div className="mt-5 d-flex flex-wrap justify-content-around gap-4">
          <div style={{ maxWidth: "300px", margin: "0 auto" }}>
            <h2>Sati po predmetu</h2>
            <div className="mb-3">
              <label>Odaberite mjesec: </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="form-control w-auto d-inline-block ms-2"
              >
                <option value="">Odaberite mjesec</option>
                {availableMonths.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ maxWidth: "300px", margin: "0 auto" }}>
              {selectedMonth && Object.keys(subjectTotals).length > 0 ? (
                <Pie data={pieChartSubjectData} />
              ) : (
                <p>Odaberite mjesec s podacima za prikaz grafikona.</p>
              )}
            </div>
          </div>
          <div style={{ maxWidth: "300px", margin: "0 auto" }}>
            <h2>Sati po profesoru</h2>
            <Pie data={pieChartTeacherData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PastTermsTable;
